import { isEmployee, isManagerOrAdmin, isSuperAdmin } from './userTypes';
import { hasAnyAccessPermission, canViewOtherUser, isViewingSelf } from './utils';
import { PERMISSION_VIEW_AND_EDIT } from './constants';

const hasOwnRoleVisibility = (user) => Boolean(user.userRole?.permissions?.ownRoleVisibility);

const hasOtherRoleVisibility = (user) => Boolean(user.userRole?.permissions?.otherRoleVisibility);

const hasPermission = (permissions) => {
  return permissions?.view?.length > 0 || permissions?.viewAndEdit?.length > 0;
};

const checkKey = (permissions, id) => {
  if (permissions?.view?.includes(id)) {
    return 'view_only';
  }
  if (permissions?.viewAndEdit?.includes(id)) {
    return 'view_and_edit';
  }
  return 'no_access';
};

const canViewAnyOwnCustomTables = (user) => {
  const permissions = user.userRole?.permissions?.ownRoleVisibility || {};
  return hasPermission(permissions);
};

const canViewAnyOtherCustomTables = (user) => {
  const permissions = user.userRole?.permissions?.otherRoleVisibility || {};
  return hasPermission(permissions);
};

const canViewOwnCustomTable = (tableId, user) => {
  return hasAnyAccessPermission(checkKey(user.userRole?.permissions?.ownRoleVisibility, tableId));
};

const canViewOtherCustomTable = (tableId, user) => {
  return hasAnyAccessPermission(checkKey(user.userRole?.permissions?.otherRoleVisibility, tableId));
};

const canEditOwnCustomTable = (tableId, user) => {
  return checkKey(user.userRole?.permissions?.ownRoleVisibility, tableId) === PERMISSION_VIEW_AND_EDIT;
};

const canEditOtherCustomTable = (tableId, user) => {
  return checkKey(user.userRole?.permissions?.otherRoleVisibility, tableId) === PERMISSION_VIEW_AND_EDIT;
};

const isRoleOptionEnabled = (option, user) => {
  return Boolean(user.userRole?.permissions?.otherRoleVisibility[option]);
};

export const canViewCustomTableById = (tableId, user, employee = null) => {
  if (isSuperAdmin(user)) return true;
  if (isViewingSelf(user, employee)) {
    return hasOwnRoleVisibility(user) && canViewOwnCustomTable(tableId, user);
  }
  return canViewOtherUser(user, employee) && hasOtherRoleVisibility(user) && canViewOtherCustomTable(tableId, user);
};

export const canEditCustomTableById = (tableId, user, employee = null) => {
  if (isSuperAdmin(user)) return true;
  if (isViewingSelf(user, employee)) {
    return hasOwnRoleVisibility(user) && canEditOwnCustomTable(tableId, user);
  }

  return canViewOtherUser(user, employee) && hasOtherRoleVisibility(user) && canEditOtherCustomTable(tableId, user);
};

export const canPerformCustomTask = (task, user, employee) => {
  if (isViewingSelf(user, employee)) {
    return false;
  }
  return canViewOtherUser(user, employee) && isRoleOptionEnabled(task, user);
};

const companyCheck = (company) => {
  return company?.baseDetails?.customTablesCount <= 0 || !company?.baseDetails?.isUsingCustomTable;
};

const employeeOtherCheck = (user, checkOwnPermissions) =>
  checkOwnPermissions ? canViewAnyOwnCustomTables(user) : canViewAnyOtherCustomTables(user);

const canViewAnyOtherTableOrRole = (user, employee) => {
  if (hasOtherRoleVisibility(user)) {
    if (isEmployee(user) || (isManagerOrAdmin(user) && canViewOtherUser(user, employee))) {
      return canViewAnyOtherCustomTables(user);
    }
  }
  return false;
};

// Used for higher level
export const canViewAnyTableOrRole = (user, employee, company, checkOwnPermissions) => {
  if (companyCheck(company)) {
    return false;
  }
  if (isSuperAdmin(user)) return true;

  if (isViewingSelf(user, employee)) {
    if (hasOwnRoleVisibility(user)) {
      return canViewAnyOwnCustomTables(user);
    }
    if (isEmployee(user) && hasOtherRoleVisibility(user)) {
      employeeOtherCheck(user, checkOwnPermissions);
    }
  }
  if (user.id !== employee.id) {
    return canViewAnyOtherTableOrRole(user, employee);
  }

  return false;
};
